<template>
  <div class="index" />
</template>

<script>

/**
 * Vue: Index
 * -----------------------------------------------------------------------------
 * Used to set index states and global event listeners.
 *
 */
import { mapActions, mapState } from 'vuex'
import { nextTick } from 'vue'

import breakpoints from '~/config/breakpoints'
import { debounce, throttle } from '~/helpers/general'

export default {
  name: 'Index',

  data() {
    return {
      global: true,
      screen: {
        breakpoint: 'xs',
        mobile: true,
        height: 0,
        width: 0,
      },
      scroll: {
        direction: 'none',
        header: 'open',
        top: true,
        x: 0,
        y: 0,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlays: (state) => state.overlays.active,
      settings: (state) => state.index.settings,
    }),
  },

  mounted() {
    this.rehydrateSite()
    this.getPreviousUrl()

    this.handleResize()
    this.handleScroll(true)

    this.setEventListeners()
    this.setEventBusListeners()

    this.loadWishlist()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      refetchCart: 'cart/setResponse',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
      loadComponent: 'components/load',
      rehydrateSite: 'rehydrateSite',
      setPreviousUrl: 'setPreviousUrl',
      setScreen: 'setScreen',
      setScroll: 'setScroll',
      setStylesheet: 'setStylesheet',
    }),

    /**
     * Get previous URL from sessionStorage.
     * - State doesn't update in time if set in the `beforeunload` event.
     */
    getPreviousUrl() {
      const previousUrl = sessionStorage.getItem('previousUrl')

      if (previousUrl) {
        this.setPreviousUrl(previousUrl)
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.setStylesheetListeners()

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), this.settings.throttle),
      )

      window.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), this.settings.throttle),
      )

      /**
       * Set previous URL just before changing page.
       */
      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('previousUrl', location.pathname)
      })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {

      /**
       * Overlay listeners act as fallbacks for non-Vue components.
       */
      cnvs.EventBus.listen('overlay:open', (overlay) => {
        this.openOverlay(overlay)
      })

      cnvs.EventBus.listen('overlay:close', (overlay) => {
        this.closeOverlay(overlay)
      })

      cnvs.EventBus.listen('Wishlist:productAdded', () => this.handleWishlistAddToCart())
    },

    /**
     * Listen for stylesheets loading to trigger events after they've loaded.
     */
    setStylesheetListeners() {
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')]

      stylesheets.forEach((element) => {
        const media = element.getAttribute('media')
        let stylesheet = element.getAttribute('href')

        if (stylesheet.includes('cdn.shopify')) {
          stylesheet = stylesheet
            .split('/')
            .reverse()[0]
            .split('?')[0]
        }

        if (!stylesheet) {
          return
        }

        /**
         * If it's already loaded then add to state.
         */
        if (media === 'all') {
          this.setStylesheet(stylesheet)
          return
        }

        /**
         * Otherwise listen for load.
         */
        element.addEventListener('load', () => {
          element.setAttribute('media', 'all')
          this.setStylesheet(stylesheet)
        })
      })
    },

    /**
     * Handle resize.
     */
    handleResize() {
      this.screen = {
        height: window.visualViewport.height,
        width: window.innerWidth,
      }

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${this.screen.height}px`,
      )

      if (window.matchMedia(`(min-width: ${breakpoints.xl})`).matches) {
        this.screen.breakpoint = 'xl'
      } else if (window.matchMedia(`(min-width: ${breakpoints.l})`).matches) {
        this.screen.breakpoint = 'l'
      } else if (window.matchMedia(`(min-width: ${breakpoints.m})`).matches) {
        this.screen.breakpoint = 'm'
      } else if (window.matchMedia(`(min-width: ${breakpoints.s})`).matches) {
        this.screen.breakpoint = 's'
      } else {
        this.screen.breakpoint = 'xs'
      }

      this.screen.mobile = Boolean(
        this.screen.breakpoint !== 'l' &&
        this.screen.breakpoint !== 'xl',
      )

      cnvs.EventBus.emit('cnvs:resize', (this.screen))
      this.setScreen(this.screen)
    },

    /**
     * Handle scroll.
     * - Don't update state when overlay opens.
     * @param {Boolean} [start] - If setting on initial load.
     */
    handleScroll(start = false) {
      if (this.activeOverlays.length) {
        return
      }

      /**
       * Set object on start.
       */
      if (start) {
        this.scroll = {
          direction: 'none',
          header: 'open',
          top: window.scrollY <= this.settings.start,
          x: window.scrollX,
          y: window.scrollY,
        }

        return
      }

      /**
       * If scroll difference is less than threshold don't update.
       */
      const difference = window.scrollY - this.scroll.y

      if (Math.abs(difference) < this.settings.threshold) {
        return
      }

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        top: window.scrollY <= this.settings.start,
        x: window.scrollX,
        y: window.scrollY,
      }

      if (this.scroll.direction === 'up' && !this.scroll.top) {
        this.scroll.header = 'minimised'
      } else if (window.scrollY > this.settings.start) {
        this.scroll.header = 'collapsed'
      } else {
        this.scroll.header = 'open'
      }

      cnvs.EventBus.emit('cnvs:scroll', (this.scroll))
      this.setScroll(this.scroll)
    },

    /**
     * Manually load wishilst functionality.
     * - If wishlist is ready, call the load event.
     * - Otherwise, intermittently check if the wishlist is ready.
     * - If so clear the interval and load the wishlist.
     * - Wishlist check cancels after a timeout to prevent constantly checking.
     *
     */
    loadWishlist() {
      if (window.Appmate?.load) {
        window.Appmate.load()
        return
      }

      let timeOut = null

      const checkForWishlist = setInterval(() => {
        if (window.Appmate?.load) {
          clearInterval(checkForWishlist)
          clearTimeout(timeOut)
          window.Appmate.load()
          return
        }
      }, 300)

      timeOut = setTimeout(() => {
        clearInterval(checkForWishlist)
      }, 5000)

    },

    /**
     * Opens the cart drawer when a Wishlist item is added to the cart.
     */
    async handleWishlistAddToCart() {
      await this.refetchCart()

      nextTick(() => {
        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        })
      })
    },
  },
}
</script>
